import type { Account } from "@/features/account/types";

import type { AccountDto, Decimal128 } from "./dto";

function isDecimal128(value: unknown): value is Decimal128 {
  return (
    typeof value === "object" &&
    value !== null &&
    "$numberDecimal" in value &&
    typeof (value as Decimal128).$numberDecimal === "string"
  );
}

/** Normalize the backend's number-ish shapes (number | string | Decimal128). */
export function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number")
    return Number.isFinite(value) ? value : fallback;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }
  if (isDecimal128(value)) return toNumber(value.$numberDecimal, fallback);
  return fallback;
}

/** Backend DTO → domain Account. Defensive: every field has a sane default. */
export function mapAccount(dto: AccountDto): Account {
  const fname = dto.fname?.trim() ?? "";
  const lname = dto.lname?.trim() ?? "";
  const fullName =
    [fname, lname].filter(Boolean).join(" ") || (dto.username ?? "");

  return {
    userId: dto.user_id ?? "",
    username: dto.username ?? "",
    email: dto.email ?? "",
    userType: dto.usertype ?? "user",
    fullName,
    avatarUrl: dto.avatar ? dto.avatar : null,
    balance: toNumber(dto.balance, 0),
    rating: toNumber(dto.rate, 0),
    premium: Boolean(dto.premium),
    topSeller: Boolean(dto.top_seller),
    satisfiedTasks: dto.satisfied_tasks ?? 0,
    countryId: dto.country_id ?? "",
    createdAt: dto.created ?? null,
  };
}
